import {
  useStore,
  type FitViewOptions,
  type Rect,
  type XYPosition,
} from '@xyflow/react';
import { useEffect, useRef } from 'react';

import { useReadableFitView } from './useReadableFitView';

interface UseInitialFitParams {
  nodes: ReadonlyArray<{ position: XYPosition }>;
  nodeSize: { width: number; height: number };
  padding?: FitViewOptions['padding'];
  minZoom: number;
  contentBounds?: Rect;
}

// Fits once per data set; a resize keeps the user's pan and zoom instead of re-fitting.
function useInitialFit(params: Readonly<UseInitialFitParams>) {
  const fit = useReadableFitView(params);
  const nodeCount = useStore((s) => s.nodeLookup.size);
  const canvasWidth = useStore((s) => s.width);
  const canvasHeight = useStore((s) => s.height);
  const fittedNodes = useRef<UseInitialFitParams['nodes'] | null>(null);
  const { nodes } = params;

  useEffect(() => {
    if (
      nodeCount === 0 ||
      canvasWidth === 0 ||
      canvasHeight === 0 ||
      fittedNodes.current === nodes
    )
      return undefined;
    const frame = requestAnimationFrame(() => {
      fit();
      fittedNodes.current = nodes;
    });
    return () => cancelAnimationFrame(frame);
  }, [fit, nodes, nodeCount, canvasWidth, canvasHeight]);
}

export { useInitialFit };
